/**
 * Plain-English lookup for the scoring pattern chips shown on every round
 * (`formatPatternBreakdown` in helpers.ts) — keyed by `PatternHit.key` from
 * `src/rules/hkos/patterns.ts`, not by the rendered label, since several
 * labels carry a dynamic tile suffix (e.g. "Dragon Pung (7z)", "Seat Flower
 * (F1)") that would make a label-keyed lookup miss.
 *
 * Descriptions for the settings-toggle-driven patterns are deliberately
 * reworded from (not copy-pasted from) `views/settings.ts`'s TOGGLE_FIELDS —
 * those are written as a toggle's effect ("Bonus fan for..."), these are
 * written as why a specific hand just earned it.
 */

import { esc } from './helpers';

export const PATTERN_GLOSSARY: Record<string, string> = {
  heavenlyHand:
    'The dealer wins with their own untouched starting hand — before drawing or discarding a single tile. A forced limit hand.',
  earthlyHand:
    "A non-dealer wins by claiming the dealer's very first discard, before their own first draw. A forced limit hand.",
  winDoubleKong:
    'A self-drawn win off a replacement tile, where the kong that produced it was itself completed from an earlier kong\'s own replacement tile.',
  thirteenOrphans:
    'One of each terminal and honour tile (1 and 9 of every suit, all four winds, all three dragons), plus one more of any of them as the pair. A forced limit hand.',
  sevenShiftedPairs:
    'Seven pairs, all one suit, in one unbroken run of ranks (e.g. 11-22-33-44-55-66-77). A forced limit hand, the same tier as Thirteen Orphans.',
  sevenPairs:
    'Seven pairs of any tiles — mixed suits are fine. An ordinary pattern, not a forced limit: it stacks with Full Flush, Half Flush, All Honours, Concealed Hand, and everything else. A kong can never be part of it — that would turn one of the pairs into four-of-a-kind.',
  allHonours: 'Every tile in the hand is a wind or a dragon — no numbered tiles at all. A limit hand worth 10 faan on its own — no wind, dragon, flower or other faan is added.',
  orphans: 'Every tile is a 1 or a 9 — no honours, no other numbers. A limit hand worth 10 faan on its own — no wind, dragon, flower or other faan is added.',
  mixedOrphans:
    'Every tile is a 1, a 9, or an honour — with at least one of each. Always stacks with All Pungs, since no chow is possible with only these tiles.',
  allKongs: 'All four sets are kongs, plus a pair. A forced limit hand.',
  greatDragons:
    'A pung or kong of all three dragon tiles — White, Green, and Red. Replaces the Dragon Pung fan those melds would otherwise earn, and replaces All Pungs too if the 4th meld also happens to be a pung — never stacks with either.',
  greatWinds: 'A pung or kong of all four wind tiles — East, South, West, and North. A forced limit hand.',
  nineGates: 'Concealed, one suit only: 1112345678999 plus one more tile of that same suit. A limit hand worth 10 faan on its own — no wind, dragon, flower or other faan is added.',
  smallDragons:
    'A pung or kong of two dragon tiles, plus a pair of the third dragon. Replaces the Dragon Pung fan those two melds would otherwise earn, and replaces All Pungs too if the 4th meld also happens to be a pung — never stacks with either.',
  smallWinds:
    'A pung or kong of three wind tiles, plus a pair of the fourth wind. Replaces All Pungs if the 4th meld also happens to be a pung, rather than stacking with it — still stacks with Half Flush. If the 4th meld is an honour instead, the whole hand is All Honours (a forced limit) and wins outright.',
  selfTriplets:
    'All four sets are concealed pungs or kongs. A limit hand worth 10 faan on its own, with no other faan added — won by self-draw, or by a discard that completed the pair (a discard that completed one of the triplets instead doesn\'t qualify).',
  fullFlush: 'All 14 tiles are one numbered suit, with no honour tiles mixed in.',
  halfFlush: 'All 14 tiles are one numbered suit, plus any winds or dragons.',
  concealedHand: 'Every set was formed from your own drawn tiles — nothing claimed from a discard.',
  selfDraw: 'Won by drawing the winning tile yourself, not by claiming another player’s discard.',
  flowers: 'A flat bonus for each flower or season tile held, regardless of which numbers they are.',
  seatFlowerBonus:
    "This flower or season's printed number matches your own seat (1 = East, 2 = South, 3 = West, 4 = North).",
  allFlowers: 'Holding all 4 flower tiles. Does not stack with Seat Flower for the matching flower.',
  allSeasons: 'Holding all 4 season tiles. Does not stack with Seat Flower for the matching season.',
  noFlowerBonus:
    'A bonus for holding zero flower or season tiles at all — the mirror image of the flat flower bonus above.',
  robbingKong: 'Won on the very tile another player just used to upgrade a pung into a kong.',
  kongReplacement: 'Self-drawing the win off the replacement tile right after declaring a kong.',
  lastWallTile: 'Won on the very last tile drawable from the wall.',
  lastDiscard: 'Won by claiming the very last discard of the game, before the wall runs out.',
  allSequences: 'Every set in the hand is a chow (a run of three) — no pungs or kongs at all.',
  allPungs: 'Every set in the hand is a pung or kong — no chows at all.',
  dragonPung:
    'A pung or kong of a dragon tile — White, Green, or Red. Not awarded on top of Great Dragons or Small Dragons, which already cover those same melds.',
  seatWindPung: 'A pung or kong of your own seat wind tile.',
  prevailingWindPung: "A pung or kong of the round's prevailing wind tile.",
};

let overlay: HTMLDivElement | undefined;

function close(): void {
  overlay?.remove();
  overlay = undefined;
}

function open(label: string, description: string): void {
  close();
  const el = document.createElement('div');
  el.className = 'lightbox-overlay';
  el.innerHTML = `
    <div class="card modal-panel">
      <h2 style="margin-top:0">${esc(label)}</h2>
      <p style="margin-bottom:0">${esc(description)}</p>
      <button type="button" class="secondary" style="width:100%;margin-top:1rem" data-close-glossary>Close</button>
    </div>
  `;
  // Tapping the backdrop closes it; tapping inside the panel shouldn't.
  el.addEventListener('click', close);
  el.querySelector('.modal-panel')!.addEventListener('click', (e) => e.stopPropagation());
  el.querySelector('[data-close-glossary]')!.addEventListener('click', close);
  document.body.appendChild(el);
  overlay = el;
}

/**
 * Wires up tap-to-explain for every current and future .pattern-chip button —
 * delegated on `document` (same technique as `initPhotoLightbox`) so it
 * works across every screen that renders round patterns (play, summary, the
 * guest device) without each one needing its own listener. Call once at
 * startup.
 */
export function initPatternGlossary(): void {
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.pattern-chip');
    if (!target) return;
    const key = target.dataset.patternKey;
    const label = target.dataset.patternLabel;
    const description = key ? PATTERN_GLOSSARY[key] : undefined;
    if (!label || !description) return;
    open(label, description);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}
